import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App.tsx";
import "./styles/global.scss";
import "config/i18n";
import { AuthProvider } from "react-oidc-context";
import { User, WebStorageStateStore } from "oidc-client-ts";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { axiosInstance } from "api/axios";
import { resilientLocalStorage } from "utils/resilientLocalStorage";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { ErrorBoundary } from "components/ErrorBoundary";
import { PushNotifications } from "lib/sw-helpers.js";
import { installInputAppearance } from "utils/inputAppearance";

// Zastosuj zapisaną preferencję wyglądu pól (Input) i nasłuchuj zmian — globalnie.
installInputAppearance();
// import { plPL } from "@mui/material/locale";

const oidcConfig = {
  authority: import.meta.env.VITE_APP_OIDC_AUTHORITY!,
  client_id: import.meta.env.VITE_APP_OIDC_CLIENTID!,
  response_type: "code",
  redirect_uri: import.meta.env.VITE_APP_SITE,
  post_logout_redirect_uri: import.meta.env.VITE_APP_SITE!,
  scope: import.meta.env.VITE_APP_OIDC_SCOPE!,
  loadUserInfo: true,
  // I żeton sesji, I przejściowy stan logowania (klucz `oidc.<state>` + weryfikator
  // PKCE) trzymamy w localStorage — tak jak pierwotnie, bo callback po powrocie z
  // IdP musi odczytać DOKŁADNIE ten sam store, w którym `signinRedirect` zapisał
  // stan (sessionStorage potrafił go zgubić → biały ekran na URL `?code=…&state=…`).
  // Odporny wrapper rozwiązuje pierwotną usterkę „exceeded the quota": gdy
  // localStorage jest przepełniony danymi aplikacji (np. tła Biurka), zwalnia
  // miejsce zamiast blokować zapis stanu/żetonu logowania.
  userStore: new WebStorageStateStore({ store: resilientLocalStorage }),
  stateStore: new WebStorageStateStore({ store: resilientLocalStorage }),

  onSigninCallback: (_user: User | void): void => {
    if (_user?.state) {
      const s = _user.state as { invitation?: string };
      if (s.invitation) {
        axiosInstance.post("/workspace/invitation/approve", {
          Key: s.invitation,
        });
      }
    }

    if (_user) {
      const idp = _user?.profile["idp"];

      window.localStorage.setItem("nota.last.uid", _user.profile.sub);

      if (idp) {
        if (idp instanceof Array) {
          window.localStorage.setItem("nota.last.idp", idp[0] ?? "unknown");
        }
        window.localStorage.setItem(
          "nota.last.idp",
          (idp as string) ?? "unknown",
        );
      }
    }

    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

const theme = createTheme({
  typography: {
    fontFamily: "Rubik",
  },
  palette: {
    primary: {
      light: "#6a57cb",
      main: "#5d49c7",
      dark: "#6a57cb",
      contrastText: "#fff",
    },
    secondary: {
      light: "#F1EBFB",
      main: "#e5dff9",
      dark: "#CDC7E0",
      contrastText: "#190064",
    },
    error: {
      light: "#BA1A1A",
      main: "#BA1A1A",
      dark: "#BA1A1A",
    },
  },
});

// ── Przechwycenie powrotu z zewnętrznego popupu OAuth (np. Microsoft) ──────────
// Popup wraca na nasz origin z `?code=…`. Gdyby uruchomiła się tu cała aplikacja
// z OIDC, ten `code` zostałby potraktowany jako logowanie OIDC i usunięty z URL
// (onSigninCallback → history.replaceState). Dlatego, gdy jesteśmy w oknie
// otwartym przez `window.open` (jest `window.opener`) i w adresie jest `code`/
// `error`, którego `state` NIE należy do trwającego logowania OIDC — przekazujemy
// wynik do okna nadrzędnego przez postMessage i zamykamy popup, bez startu App.
const oauthParams = new URLSearchParams(window.location.search);
const oauthCode = oauthParams.get("code");
const oauthError = oauthParams.get("error");
const oauthState = oauthParams.get("state");
// OIDC trzyma stan logowania w localStorage pod kluczem zawierającym `state`.
const isOidcCallback =
  !!oauthState &&
  Object.keys(window.localStorage).some((k) => k.includes(oauthState));

if (window.opener && (oauthCode || oauthError) && !isOidcCallback) {
  try {
    window.opener.postMessage(
      {
        type: "oauth-callback",
        code: oauthCode,
        error: oauthError,
        errorDescription: oauthParams.get("error_description"),
        state: oauthState,
      },
      window.location.origin,
    );
  } catch {
    /* okno nadrzędne niedostępne — ignorujemy */
  }
  window.close();
} else {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <AuthProvider {...oidcConfig}>
          <GoogleOAuthProvider clientId="865926315578-6s2396takuu2uusi2r4p3avh29pq6142.apps.googleusercontent.com">
            <App></App>
          </GoogleOAuthProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>,
  );

  PushNotifications.initSW();
}
